import type { SceneStore } from '@os/doc';
import { ApiError, type Api, type AssetUploadMeta } from '../../lib/api';
import type { AssetMeta, LocalMediaStore } from './store';
import type { StoredVariant } from './variants';

export type UploadState = 'queued' | 'uploading' | 'done' | 'failed' | 'quota' | 'forbidden';
export type UploadEntry = {
  assetId: string;
  name: string;
  state: UploadState;
  done: StoredVariant[];
  expected: StoredVariant[];
  bytesDone: number;
  bytesTotal: number;
  attempts: number;
};

type Options = {
  boardId: string;
  api: Pick<Api, 'checkAsset' | 'putAssetVariant'>;
  local: LocalMediaStore;
  store: SceneStore;
  concurrency?: number;
  sleep?: (ms: number) => Promise<void>;
};

const MAX_ATTEMPTS = 5;
const backoff = (attempt: number) => Math.min(30_000, 1000 * 2 ** (attempt - 1));
const realSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Errors that end an entry without automatic retries (spec §10.1 step 7: the user retries or cancels). */
function finalState(err: unknown): UploadState | null {
  if (!(err instanceof ApiError)) return null;
  if (err.code === 'quota') return 'quota';
  if (err.code === 'forbidden' || err.code === 'not_found') return 'forbidden';
  if (err.code === 'invalid' || err.code === 'too_large') return 'failed';
  return null; // network, unauthorized (session refresh), conflict: retried
}

const uploadMeta = (m: AssetMeta): AssetUploadMeta => ({
  sha256: m.sha256, mime: m.mime, width: m.w, height: m.h, thumbhash: m.thumbhash, expected: m.expected,
});

/**
 * Uploads of one server board, 3 at a time (spec §10.1 steps 3, 6, 7): the server's dedup first, then each missing
 * variant, recorded in IndexedDB after each success so a reload resumes where it stopped. A dedup hit (before or
 * during the upload) points the document at the existing asset instead.
 */
export class UploadQueue {
  private readonly items = new Map<string, UploadEntry>();
  private readonly listeners = new Set<() => void>();
  private running = 0;
  private destroyed = false;

  constructor(private readonly o: Options) {}

  enqueue(assetId: string, name: string): void {
    const existing = this.items.get(assetId);
    if (existing && existing.state !== 'failed') return;
    if (!this.add(assetId, name, [])) return;
    void this.o.local.putUpload({ assetId, boardId: this.o.boardId, name, done: [] });
    this.pump();
  }

  async resumePending(): Promise<void> {
    for (const r of await this.o.local.listUploads(this.o.boardId)) {
      if (this.items.has(r.assetId)) continue;
      if (!this.add(r.assetId, r.name, r.done)) await this.o.local.deleteUpload(r.assetId); // blobs gone: nothing to send
    }
    this.pump();
  }

  retry(assetId: string): void {
    const e = this.items.get(assetId);
    if (!e || (e.state !== 'failed' && e.state !== 'quota')) return;
    Object.assign(e, { state: 'queued', attempts: 0 });
    this.notify();
    this.pump();
  }

  cancel(assetId: string): void {
    if (!this.items.delete(assetId)) return;
    const ids = this.o.store.listAll().filter((o) => o.type === 'image' && o.assetId === assetId).map((o) => o.id);
    if (ids.length) this.o.store.commands.deleteObjects(ids);
    void this.o.local.deleteUpload(assetId);
    this.notify();
  }

  entries(): UploadEntry[] {
    return [...this.items.values()].map((e) => ({ ...e, done: [...e.done] }));
  }

  uploadingIds(): ReadonlySet<string> {
    return new Set([...this.items.values()].filter((e) => e.state === 'queued' || e.state === 'uploading').map((e) => e.assetId));
  }

  subscribe(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  /** Resolves once each of these uploads has ended (done, failed, quota, forbidden or cancelled). */
  settled(assetIds: readonly string[]): Promise<void> {
    const open = () => assetIds.some((id) => {
      const s = this.items.get(id)?.state;
      return s === 'queued' || s === 'uploading';
    });
    if (!open()) return Promise.resolve();
    return new Promise((resolve) => {
      const off = this.subscribe(() => {
        if (open()) return;
        off();
        resolve();
      });
    });
  }

  destroy(): void {
    this.destroyed = true;
    this.listeners.clear();
  }

  private add(assetId: string, name: string, done: StoredVariant[]): boolean {
    const meta = this.o.local.get(assetId);
    if (!meta) return false;
    const size = (v: StoredVariant) => this.o.local.blob(assetId, v)?.size ?? 0;
    this.items.set(assetId, {
      assetId, name, state: 'queued', done: [...done], expected: [...meta.expected],
      bytesDone: done.reduce((n, v) => n + size(v), 0), bytesTotal: meta.expected.reduce((n, v) => n + size(v), 0), attempts: 0,
    });
    this.notify();
    return true;
  }

  private pump(): void {
    const limit = this.o.concurrency ?? 3;
    while (!this.destroyed && this.running < limit) {
      const next = [...this.items.values()].find((e) => e.state === 'queued');
      if (!next) return;
      next.state = 'uploading';
      this.running++;
      this.notify();
      void this.run(next).finally(() => {
        this.running--;
        this.pump();
      });
    }
  }

  private live(e: UploadEntry): boolean {
    return !this.destroyed && this.items.get(e.assetId) === e;
  }

  private async run(e: UploadEntry): Promise<void> {
    const meta = this.o.local.get(e.assetId);
    if (!meta?.sha256) return this.end(e, 'failed'); // hashing unavailable (plain-HTTP LAN): the server needs the sha
    for (;;) {
      try {
        if (e.done.length === 0) {
          const existing = await this.o.api.checkAsset(this.o.boardId, meta.sha256);
          if (!this.live(e)) return;
          if (existing && existing !== e.assetId) return this.replace(e, existing);
        }
        for (const v of e.expected) {
          if (e.done.includes(v)) continue;
          const blob = this.o.local.blob(e.assetId, v);
          if (!blob) return this.end(e, 'failed');
          const r = await this.o.api.putAssetVariant({ boardId: this.o.boardId, assetId: e.assetId, variant: v, blob, meta: uploadMeta(meta) });
          if (!this.live(e)) return;
          if ('duplicateOf' in r) return this.replace(e, r.duplicateOf);
          e.done.push(v);
          e.bytesDone += blob.size;
          e.attempts = 0;
          await this.o.local.putUpload({ assetId: e.assetId, boardId: this.o.boardId, name: e.name, done: [...e.done] });
          this.notify();
        }
        await this.o.local.deleteUpload(e.assetId);
        return this.end(e, 'done');
      } catch (err) {
        if (!this.live(e)) return;
        const state = finalState(err);
        e.attempts++;
        if (state) return this.end(e, state);
        if (e.attempts >= MAX_ATTEMPTS) return this.end(e, 'failed');
        this.notify();
        await (this.o.sleep ?? realSleep)(backoff(e.attempts));
        if (!this.live(e)) return;
      }
    }
  }

  private async replace(e: UploadEntry, to: string): Promise<void> {
    this.o.store.commands.replaceAssetId(e.assetId, to);
    this.o.local.alias(e.assetId, to);
    await this.o.local.deleteUpload(e.assetId);
    this.end(e, 'done');
  }

  private end(e: UploadEntry, state: UploadState): void {
    if (!this.live(e)) return;
    e.state = state;
    this.notify();
  }

  private notify(): void {
    for (const l of [...this.listeners]) l();
  }
}
